import { keyframes } from '@emotion/react';
import { Review } from '@kizi/comparator-core';
import { Button, Div, DivProps, P } from '@kizi/core-components';
import React from 'react';
import { useTestimonials } from './hooks/reviews';
import { Path } from './Path';
import { ReviewCard } from './ReviewCard';

interface ProductTestimonialsProps extends DivProps {
  review?: Review;
  size?: number;
}

const pulseKeyframe = keyframes`
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
  90% {
    opacity: 1;
  }
`;

export const ProductTestimonials: React.VFC<ProductTestimonialsProps> = ({
  review,
  size = 2,
  ...props
}) => {
  if (!review) return null;

  const { id } = review;

  const [page, setPage] = React.useState<number>(0);
  const { testimonials, isLoading } = useTestimonials(id, page, size);

  const cardWidth = size > 1 ? '50%' : '100%';
  const isFirstPage = page === 0;
  let isLastPage;

  if (testimonials) {
    isLastPage =
      page === testimonials.page?.totalPages - 1 ||
      testimonials.items?.length !== testimonials.page?.pageSize;
  }

  return (
    <Div
      display="flex"
      flexDirection="column"
      gridGap={3}
      width="100% !important"
      {...props}
    >
      <Div
        display="flex"
        flexDirection="row"
        gridGap={6}
        alignItems="center"
        justifyContent="space-between"
      >
        <P variant="bodyImportant">Reviews van klanten</P>
        {!isLoading && testimonials && (
          <Div
            display="flex"
            flexDirection="row"
            gridGap={3}
            alignItems="center"
          >
            <Button
              variant="transparent"
              lineHeight="0px"
              onClick={(e) => {
                e.stopPropagation();
                if (!isFirstPage) {
                  setPage((p) => p - 1);
                }
              }}
              color="second.1"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                xmlns="http://www.w3.org/2000/svg"
                fill="second.1"
              >
                <Path
                  d="M8.88423 16.081C8.74146 16.081 8.61026 16.0327 8.53694 15.9536L8.52536 15.9421L1.83799 9.25468C1.74731 9.164 1.6875 9.01544 1.6875 8.88423C1.6875 8.75303 1.74731 8.60447 1.83799 8.51379L8.50028 1.8515C8.61797 1.74538 8.75496 1.6875 8.88423 1.6875C9.01351 1.6875 9.15049 1.74538 9.26819 1.8515L10.0766 2.65993L10.0882 2.6715C10.1673 2.74482 10.2155 2.87602 10.2155 3.0188C10.2155 3.16158 10.1673 3.29278 10.0882 3.36609L10.0766 3.37767L5.78559 7.6687H15.9073C16.3009 7.6687 16.3125 8.13562 16.3125 8.18964V9.57883C16.3125 9.63285 16.3009 10.0998 15.9073 10.0998H5.78559L10.0515 14.3657C10.1577 14.4834 10.2155 14.6204 10.2155 14.7497C10.2155 14.8789 10.1577 15.0159 10.0515 15.1336L9.25468 15.9305C9.164 16.0212 9.01544 16.081 8.88423 16.081Z"
                  fill={page === 0 ? 'second.1' : 'main.1'}
                />
              </svg>
            </Button>
            <Button
              variant="transparent"
              lineHeight="0px"
              onClick={(e) => {
                e.stopPropagation();
                if (!isLastPage) {
                  setPage((p) => p + 1);
                }
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <Path
                  d="M9.11577 16.081C9.25854 16.081 9.38974 16.0327 9.46306 15.9536L9.47464 15.9421L16.162 9.25468C16.2527 9.164 16.3125 9.01544 16.3125 8.88423C16.3125 8.75303 16.2527 8.60447 16.162 8.51379L9.49972 1.8515C9.38203 1.74538 9.24504 1.6875 9.11577 1.6875C8.98649 1.6875 8.84951 1.74538 8.73181 1.8515L7.92338 2.65993L7.91181 2.6715C7.8327 2.74482 7.78447 2.87602 7.78447 3.0188C7.78447 3.16158 7.8327 3.29278 7.91181 3.36609L7.92338 3.37767L12.2144 7.6687H2.09268C1.69908 7.6687 1.6875 8.13562 1.6875 8.18964V9.57883C1.6875 9.63285 1.69908 10.0998 2.09268 10.0998H12.2144L7.94847 14.3657C7.84235 14.4834 7.78447 14.6204 7.78447 14.7497C7.78447 14.8789 7.84235 15.0159 7.94847 15.1336L8.74532 15.9305C8.836 16.0212 8.98456 16.081 9.11577 16.081Z"
                  fill={
                    page === testimonials.page?.totalPages - 1 ||
                    testimonials.items?.length !== testimonials.page?.pageSize
                      ? 'second.1'
                      : 'main.1'
                  }
                />
              </svg>
            </Button>
          </Div>
        )}
      </Div>
      <Div display="flex" flexDirection="row">
        {isLoading && (
          <Div display="flex" flexDirection="row" gridGap={6} width="100%">
            {Array(size)
              .fill(0)
              .map((_, index) => (
                <Div
                  key={index}
                  display="flex"
                  flexDirection="column"
                  gridGap={3}
                  border="1px solid"
                  borderColor="second.1"
                  p={3}
                  borderRadius={2}
                  width={cardWidth}
                  height={190}
                  bg="second.2"
                  css={{
                    animation: `${pulseKeyframe} 2s infinite`,
                  }}
                />
              ))}
          </Div>
        )}
        {!isLoading && testimonials && (
          <Div display="flex" flexDirection="row" gridGap={6} width="100%">
            {testimonials.items?.map((testimonial) => (
              <ReviewCard
                testimonial={testimonial}
                key={testimonial.id}
                width={cardWidth}
              />
            ))}
          </Div>
        )}
        {!isLoading && !testimonials && (
          <P variant="footnoteRegular">
            Deze verzekeraar heeft op dit moment nog geen reviews.
          </P>
        )}
      </Div>
    </Div>
  );
};
